import { Button, Typography } from 'components/common'
import {
  Category,
  useArchiveCategoryMutation,
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation
} from 'graphql/generated'
import { useCallback, useEffect, useState } from 'react'
import ModalTrigger from 'components/common/ModalTrigger'
import BaseModal from 'components/modals/BaseModal'
import TextField from 'components/common/TextField'
import { useControlledInput } from 'hooks'
import { useCollectiveContext } from 'context/CollectiveContext'
import { useRouter } from 'next/router'
import {
  faBoxArchive,
  faPencil,
  faPlus,
  faTrash
} from '@fortawesome/pro-regular-svg-icons'
import { useConfirmPromptModal } from 'components/modals/ConfirmPromptModal'
import { useEventEmitterContext } from 'context/EventEmitterContext'
import { useForumContext } from './ForumContext'
import FieldLabel from 'components/common/FieldLabel'
import Spinner from 'components/Spinner'
import { noop } from 'lodash'
import { useAnalyticsContext } from 'context/AnalyticsContext'

const MODAL_CLOSE_EVENT = 'closeCategoryModal'

function useCategoryInputModal({
  category
}: {
  category?: Pick<
    Category,
    'id' | 'name' | 'description' | 'removable' | 'archived'
  >
}) {
  const router = useRouter()
  const { collective } = useCollectiveContext()
  const { refetchBoard } = useForumContext()
  const { loaded: analyticsLoaded } = useAnalyticsContext()

  const [name, onNameChange] = useControlledInput()
  const [description, onDescriptionChange] = useControlledInput()

  useEffect(() => {
    if (category) {
      onNameChange(category.name)
      onDescriptionChange(category.description || '')
    }
  }, [category, onDescriptionChange, onNameChange])
  const [createCategory, { loading: isCreating }] = useCreateCategoryMutation()
  const [editCategory, { loading: isUpdating }] = useUpdateCategoryMutation()
  const { emitter } = useEventEmitterContext()
  const [showModal, setShowModal] = useState<boolean>(false)
  const canSave =
    !isUpdating &&
    !isCreating &&
    !!name.trim().length &&
    collective.membership?.privileged

  const idEditMode = !!category

  const canDelete =
    !!category && !!collective.membership?.privileged && !!category?.removable

  const confirm = useConfirmPromptModal()
  const [archive, { loading: isArchiving }] = useArchiveCategoryMutation()
  const archiveCategory = useCallback(async () => {
    if (!canDelete) {
      return
    }
    confirm({
      title: `${category.archived ? 'Unarchive' : 'Archive'} Category ${
        category.name
      }?`,
      body: (
        <div className="mt-2">
          <Typography color="gray">
            {`Are you sure you want to ${
              category.archived ? 'unarchive' : 'archive'
            } this category?`}
          </Typography>

          <ul className="space-y-2 mt-2">
            <Typography component={'li'} size="sm" color="gray">
              -{' '}
              {!category.archived
                ? `Threads will be locked: No further edits, removals, or comments allowed.`
                : `Threads will be unlocked: Edits, removals, and comments will be allowed again.`}
            </Typography>
            <Typography component={'li'} size="sm" color="gray">
              -{' '}
              {!category.archived
                ? `Visible to Signators: Only Signators can still peek at the
              threads in this category.`
                : `Visible to All DAO Members: Everyone can view and interact with the threads in this category.`}
            </Typography>
          </ul>
        </div>
      ),
      cancelLabel: 'Nevermind',
      confirmLabel: 'Confirm',
      onClick: async () => {
        await archive({
          variables: {
            categoryId: category.id,
            archived: !category.archived
          },
          onCompleted: () => {
            if (analyticsLoaded) {
              window.analytics?.trackAll(`Forum - Category - Archived`, {
                categoryId: category.id,
                categoryName: name.trim(),
                archived: !category.archived
              })
            }
            refetchBoard()
          }
        })

        emitter.emit(MODAL_CLOSE_EVENT)
        setShowModal(false)
      }
    })
  }, [
    canDelete,
    confirm,
    category,
    archive,
    emitter,
    analyticsLoaded,
    refetchBoard,
    name
  ])

  const [deleteCat, { loading: isDeleting }] = useDeleteCategoryMutation()
  const deleteCategory = useCallback(async () => {
    if (!canDelete || !category) {
      return
    }
    confirm({
      title: `Delete Category ${category.name}?`,
      body: `Are you sure you want to permanently delete this category?`,
      cancelLabel: 'Nevermind',
      confirmLabel: 'Confirm',
      onClick: async () => {
        await deleteCat({
          variables: {
            categoryId: category.id
          },
          onCompleted: () => {
            if (analyticsLoaded) {
              window.analytics?.trackAll(`Forum - Category - Deleted`, {
                categoryId: category.id,
                categoryName: name.trim()
              })
            }
            router.push(`${collective.publicUrl}/discussionBoard`)
            refetchBoard()
          }
        })

        emitter.emit(MODAL_CLOSE_EVENT)
        setShowModal(false)
      }
    })
  }, [
    canDelete,
    category,
    confirm,
    deleteCat,
    emitter,
    analyticsLoaded,
    router,
    collective.publicUrl,
    refetchBoard,
    name
  ])

  const save = useCallback(async () => {
    if (!canSave) {
      return
    }

    if (!category) {
      await createCategory({
        variables: {
          discussionBoardId: collective.discussionBoard.id,
          categoryDetails: {
            name: name.trim(),
            description: description.trim().length ? description : undefined
          }
        },
        onCompleted: ({ createCategory }) => {
          if (analyticsLoaded) {
            window.analytics?.trackAll(`Forum - Category - Created`, {
              categoryName: name.trim()
            })
          }
          refetchBoard()
          router.push(createCategory.publicUrl)
        }
      })
    } else {
      await editCategory({
        variables: {
          categoryId: category.id,
          categoryDetails: {
            name: name.trim(),
            description: description.trim().length ? description : undefined
          }
        },
        onCompleted: () => {
          if (analyticsLoaded) {
            window.analytics?.trackAll(`Forum - Category - Edit`, {
              categoryId: category.id,
              oldCategoryName: category.name,
              categoryName: name.trim()
            })
          }
          refetchBoard()
        }
      })
    }
    emitter.emit(MODAL_CLOSE_EVENT)
    setShowModal(false)
  }, [
    canSave,
    category,
    emitter,
    createCategory,
    collective.discussionBoard.id,
    name,
    description,
    analyticsLoaded,
    refetchBoard,
    router,
    editCategory
  ])

  const busy = isCreating || isUpdating || isArchiving || isDeleting

  const modalContent = (
    <BaseModal onClose={() => setShowModal(false)} disableBackdropClick>
      <div className="flex-1 flex flex-col p-4 gap-4 overflow-hidden">
        <div className="flex flex-1 flex-col gap-4 overflow-auto">
          <Typography size="heading">New Category</Typography>
          <TextField
            required
            label="Name"
            value={name}
            onChange={onNameChange}
            preLabel="#"
            placeholder="e.g. Alphas"
          />

          <TextField
            label="Description"
            value={description}
            placeholder="This topic is for discussing all Alphas or Alpha suggestions"
            onChange={onDescriptionChange}
            multiple
          />

          {canDelete && (
            <>
              <div
                onClick={!isArchiving ? archiveCategory : noop}
                className="relative hover:bg-gray-100 cursor-pointer px-3 py-2 rounded-md border border-gray-200 overflow-hidden"
              >
                <FieldLabel
                  label={
                    category.archived
                      ? 'Unarchive Category'
                      : 'Archive Category'
                  }
                  className="text-red-500 px-0"
                  additionalLabelProps={{ size: 'sm', fontWeight: 'default' }}
                  icon={faBoxArchive}
                />
                <Typography color="gray" size="xs">
                  {!category.archived
                    ? `Archiving this category will hide all related threads from DAO
                members, locking them from further edits.`
                    : `Unarchiving this category will make all related threads visible to DAO members again, allowing further edits and comments.`}
                </Typography>

                {isArchiving && (
                  <div className="absolute inset-0 w-full h-full bg-gray-900/40 flex justify-center items-center">
                    <Spinner />
                  </div>
                )}
              </div>

              <div
                onClick={!isDeleting ? deleteCategory : noop}
                className="relative hover:bg-gray-100 cursor-pointer px-3 py-2 rounded-md border border-gray-200 overflow-hidden"
              >
                <FieldLabel
                  label={'Delete Category'}
                  className="text-red-500 px-0"
                  additionalLabelProps={{ size: 'sm', fontWeight: 'default' }}
                  icon={faTrash}
                />
                <Typography color="gray" size="xs">
                  Deleting this category will permanently remove all related
                  threads and they cannot be recovered.
                </Typography>

                {isDeleting && (
                  <div className="absolute inset-0 w-full h-full bg-gray-900/40 flex justify-center items-center">
                    <Spinner />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <Button
          label="Save Category"
          loading={isCreating || isUpdating}
          disabled={!canSave || busy}
          onClick={save}
        />
      </div>
    </BaseModal>
  )

  const modalTrigger = (
    <ModalTrigger
      closeOnEvent={MODAL_CLOSE_EVENT}
      trigger={show => (
        <Button
          color={!idEditMode ? 'blue' : 'outline'}
          icon={!idEditMode ? faPlus : faPencil}
          label={!idEditMode ? 'Create New Category' : 'Edit'}
          onClick={show}
          size="xs"
        />
      )}
    >
      {modalContent}
    </ModalTrigger>
  )

  return {
    modalContent,
    modalTrigger,
    setShowModal,
    showModal
  }
}

export default useCategoryInputModal
